import { defineStore } from 'pinia';
import { ref, computed } from 'vue';

export enum ASSESSMENT_DOMAIN {
    ANGULAR = 'angular',
    AZURE = 'azure',
    CAPACITOR = 'capacitor',
    CPP = 'cplusplus',
    CSHARP = 'csharp',
    CSS = 'css',
    DOTNETCORE = 'dotnetcore',
    GIT = 'git',
    HTML = 'html',
    JAVA = 'java',
    JAVASCRIPT = 'javascript',
    LINUX = 'linux',
    MAUI = 'maui',
    NGINX = 'nginx',
    NODE = 'nodejs',
    OPENCLAW = 'openclaw',
    PHP = 'php',
    PYTHON = 'python',
    REACTJS = 'reactjs',
    RUBY = 'ruby',
    SELENIUM = 'selenium',
    SQL = 'sql',
    TYPESCRIPT = 'typescript',
    VUE = 'vuejs'
}

/**
 * Human-friendly display names for language/domain keys.
 * Use these in the UI when showing product/assessment titles.
 */
export const ASSESSMENT_DOMAIN_DISPLAY_NAMES: Record<ASSESSMENT_DOMAIN, string> = {
    angular: 'Angular',
    azure: 'Azure',
    capacitor: 'Capacitor',
    cplusplus: 'C++',
    csharp: 'C#',
    css: 'CSS',
    dotnetcore: '.NET Core',
    git: 'Git',
    html: 'HTML',
    java: 'Java',
    javascript: 'JavaScript',
    linux: 'Linux',
    maui: '.NET MAUI',
    nginx: 'NGINX',
    nodejs: 'Node.js',
    openclaw: 'OpenClaw',
    php: 'PHP',
    python: 'Python',
    reactjs: 'React.js',
    ruby: 'Ruby',
    selenium: 'Selenium',
    sql: 'SQL',
    typescript: 'TypeScript',
    vuejs: 'Vue.js'
};


export const ASSESSMENT_DOMAIN_LOGOS: Record<ASSESSMENT_DOMAIN, string> = {
    angular: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/angular/angular-original.svg',
    azure: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/azure/azure-original.svg',
    capacitor: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/capacitor/capacitor-original.svg',
    cplusplus: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/cplusplus/cplusplus-original.svg',
    csharp: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/csharp/csharp-original.svg',
    css: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/css3/css3-plain-wordmark.svg',
    dotnetcore: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/dotnetcore/dotnetcore-original.svg',
    git: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/git/git-plain-wordmark.svg',
    html: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/html5/html5-plain-wordmark.svg',
    java: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/java/java-original-wordmark.svg',
    javascript: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/javascript/javascript-original.svg',
    linux: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/linux/linux-original.svg',
    maui: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/dot-net/dot-net-original.svg',
    nginx: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/nginx/nginx-original.svg',
    nodejs: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/nodejs/nodejs-original-wordmark.svg',
    openclaw: 'https://cdn.jsdelivr.net/gh/homarr-labs/dashboard-icons/svg/openclaw.svg',
    php: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/php/php-plain.svg',
    python: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/python/python-original-wordmark.svg',
    reactjs: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/react/react-original-wordmark.svg',
    ruby: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/ruby/ruby-plain.svg',
    selenium: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/selenium/selenium-original.svg',
    sql: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/mysql/mysql-plain.svg',
    typescript: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/typescript/typescript-original.svg',
    vuejs: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/vuejs/vuejs-original-wordmark.svg'
};

/**
 * Global store for app-wide settings such as the current assessment language.
 *
 * Language resolution order (best-effort):
 * 1. Vite env var `VITE_ASSESSMENT_DOMAIN` (useful for local testing)
 * 2. localStorage override (key: 'app:language')
 * 3. Hostname subdomain (eg. 'javascript.domain.com' -> 'javascript')
 * 4. deployment YAML (fetched from common paths)
 * 5. fallback: 'javascript'
 */
export const useGlobalStore = defineStore( 'global', () => {
    const language = ref<string>( 'javascript' );

    const normalized = ( l: string | null | undefined ) => {
        if ( !l ) return '';
        return String( l ).trim().toLowerCase();
    };

    const languageNormalized = computed( () => normalized( language.value ) );

    /**
     * Computed human-friendly display name for the currently selected language.
     * Falls back to a capitalized form of the normalized key or 'JavaScript'.
     */
    const displayDomainName = computed( () => {
        const key = languageNormalized.value;
        if ( key && key in ASSESSMENT_DOMAIN_DISPLAY_NAMES ) return ASSESSMENT_DOMAIN_DISPLAY_NAMES[key as keyof typeof ASSESSMENT_DOMAIN_DISPLAY_NAMES];
        if ( key ) return key.charAt( 0 ).toUpperCase() + key.slice( 1 );
        return 'JavaScript';
    } );

    /**
     * Computed title strings for reuse across the app.
     */
    const pageTitleHeading = computed( () => {
        return `Build with HTML5 - ${displayDomainName.value} Assessments`;
    } );

    const documentTitle = computed( () => {
        return `Build with HTML5 - ${displayDomainName.value} Assessment Platform`;
    } );

    /**
     * Computed URL for the logo image that corresponds to the currently
     * selected language/domain. Falls back to the JavaScript logo.
     */
    const getDomainLogoImage = computed( () => {
        const key = languageNormalized.value;
        if ( key && key in ASSESSMENT_DOMAIN_LOGOS ) return ASSESSMENT_DOMAIN_LOGOS[key as keyof typeof ASSESSMENT_DOMAIN_LOGOS];
        return ASSESSMENT_DOMAIN_LOGOS.javascript;
    } );

    function detectFromHostname (): string | null {
        try {
            const host = typeof window !== 'undefined' ? window.location.hostname : '';
            if ( !host ) return null;

            // treat common local dev hosts as non-subdomain
            if ( host === 'localhost' || host === '127.0.0.1' || /^\d+\.\d+\.\d+\.\d+$/.test( host ) ) {
                return null;
            }

            // hostname like 'javascript.domain.com' -> 'javascript'
            const parts = host.split( '.' );
            if ( parts.length >= 3 ) {
                // subdomain exists
                return normalized( parts[0] );
            }

            // If hostname is like 'javascript' (rare), return it
            if ( parts.length === 1 ) return normalized( parts[0] );

            return null;
        } catch ( e ) {
            return null;
        }
    }

    function getEnvDefault (): string | null {
        try {
            // Vite exposes env vars as import.meta.env.VITE_*
            // Use a fallback of undefined when not available
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const v = import.meta.env && import.meta.env.VITE_ASSESSMENT_DOMAIN;
            return normalized( v ) || null;
        } catch ( e ) {
            return null;
        }
    }

    function getLocalOverride (): string | null {
        try {
            if ( typeof window === 'undefined' ) return null;
            const v = localStorage.getItem( 'app:language' );
            return normalized( v ) || null;
        } catch ( e ) {
            return null;
        }
    }

    /**
     * Try to read a deployment YAML config for a default language.
     * We'll attempt a few common paths but don't hard-code a server layout.
     * This is a tiny, dependency-free parser that looks for `defaultLanguage: <value>`.
     */
    async function getDeployDefault (): Promise<string | null> {
        // Allow deployments to override the candidate paths via an env var
        // Vite env: VITE_DEPLOY_CONFIG_PATHS can be a comma-separated list of paths
        // e.g. "/deploy/app-config.yaml,/app-config.yaml"
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const envPaths = import.meta.env && import.meta.env.VITE_DEPLOY_CONFIG_PATHS;

        const paths = envPaths
            ? String( envPaths )
                .split( ',' )
                .map( p => p.trim() )
                .filter( Boolean )
            : [
                '/deploy/app-config.yaml',
                '/app-config.yaml',
                '/deploy/app-config.yml',
                '/app-config.yml'
            ];

        for ( const p of paths ) {
            try {
                const r = await fetch( p );
                if ( !r.ok ) continue;
                const txt = await r.text();
                // simple yaml parsing for a single key: defaultLanguage: value
                // handles: defaultLanguage: php  OR defaultLanguage: "php"
                const m = txt.match( /^[ \t]*defaultLanguage:\s*(?:"|')?([a-zA-Z0-9_+-]+)(?:"|')?/m );
                if ( m && m[1] ) {
                    return normalized( m[1] );
                }
            } catch ( e ) {
                // ignore and try next
            }
        }

        return null;
    }

    /**
     * Initialize the language value using the preferred resolution order.
     * Resolution order:
     * 1. localStorage override
     * 2. hostname subdomain
     * 3. deployment YAML (fetched from common paths)
    * 4. Vite env var `VITE_ASSESSMENT_DOMAIN`
     * 5. fallback 'javascript'
     *
     * This function is async because it may need to fetch the deployment YAML.
     */
    async function initLanguage (): Promise<void> {
        // Prefer an explicit Vite-provided environment override for scripted
        // development and CI. This allows `VITE_ASSESSMENT_DOMAIN=html` to
        // reliably select the domain even if a developer previously set a
        // browser localStorage override during manual testing.
        const env = getEnvDefault();
        if ( env ) {
            language.value = env;
            return;
        }

        // local override
        const local = getLocalOverride();
        if ( local ) {
            language.value = local;
            return;
        }

        // try hostname (URL subdomain has priority after local overrides)
        const hostLang = detectFromHostname();
        if ( hostLang ) {
            language.value = hostLang;
            return;
        }

        // attempt to read a deployment config
        const deployLang = await getDeployDefault();
        if ( deployLang ) {
            language.value = deployLang;
            return;
        }

        // fallback
        language.value = 'javascript';
    }

    /**
     * Manually set language at runtime. If `persist` is true the choice is saved
     * to localStorage so it can be used during subsequent page loads (dev/test).
     */
    function setLanguage ( l: string, persist = false ) {
        const n = normalized( l ) || 'javascript';
        language.value = n;
        if ( persist && typeof window !== 'undefined' ) {
            try {
                localStorage.setItem( 'app:language', n );
            } catch ( e ) {
                // ignore
            }
        }
    }

    function clearLocalOverride () {
        try {
            if ( typeof window === 'undefined' ) return;
            localStorage.removeItem( 'app:language' );
        } catch ( e ) {
            // ignore
        }
    }

    return {
        language,
        languageNormalized,
        displayDomainName,
        pageTitleHeading,
        documentTitle,
        getDomainLogoImage,
        initLanguage,
        setLanguage,
        clearLocalOverride,
        detectFromHostname,
        getEnvDefault,
        getLocalOverride
    };
} );

export default useGlobalStore;
